import { swapAuthLinks } from './auth-link-swap'

/** The flyout and link-panel elements the top-bar links drive, by their shared public API. */
interface NavPanel extends HTMLElement {
	open(): void
	collapse?(): void
	cancelPendingOpen?(): void
}

/**
 * Navigation
 */
export class GMXNavigation extends HTMLElement {
	hamburgerMenuBtn: HTMLButtonElement
	hamburgerMenuCloseBtn: HTMLButtonElement
	hamburgerMenuContainer: HTMLElement
	submenuSidebar: HTMLElement
	backgroundHandler: HTMLElement
	topBarLinks: HTMLElement[] = []

	private menuOpener: HTMLElement | null = null

	private boundHamburgerMenuBtnClick = this._handleHamburgerMenuClick.bind(this)
	private boundHamburgerMenuCloseBtnClick = this._handleHamburgerMenuCloseBtnClick.bind(this)
	private boundBackgroundHandlerClick = this._handleBackgroundClick.bind(this)
	private boundTopBarLinkClick = this._handleTopBarLinkClick.bind(this)
	private boundTopBarLinkKeydown = this._handleTopBarLinkKeydown.bind(this)
	private boundTabTrap = this._handleTabTrap.bind(this)

	connectedCallback(): void {
		this.hamburgerMenuBtn = this.querySelector('button.hamburger-menu') as HTMLButtonElement
		this.hamburgerMenuCloseBtn = this.querySelector('button.close-btn') as HTMLButtonElement
		this.hamburgerMenuContainer = this.querySelector('.hamburger-submenu-container') as HTMLElement
		this.submenuSidebar = this.querySelector('gmx-navigation-submenu') as HTMLElement
		this.backgroundHandler = this.querySelector('.background-handler') as HTMLElement
		this.topBarLinks = Array.from(this.querySelectorAll<HTMLElement>('.nav-top-links .nav-top-link'))

		this.hamburgerMenuBtn.addEventListener('click', this.boundHamburgerMenuBtnClick)
		this.hamburgerMenuCloseBtn.addEventListener('click', this.boundHamburgerMenuCloseBtnClick)
		this.backgroundHandler.addEventListener('click', this.boundBackgroundHandlerClick)
		this.topBarLinks.forEach(link => {
			link.addEventListener('click', this.boundTopBarLinkClick)
			link.addEventListener('keydown', this.boundTopBarLinkKeydown)
		})

		//remove preload class to restore animation timing, which is forced to 0 to prevent animations running on page load
		this.submenuSidebar.classList.remove('preload')

		// Handles a nav that upgrades after GBPE has already resolved the session.
		swapAuthLinks()
	}

	disconnectedCallback(): void {
		//todo: do we need this? dom elements are never removed so I don't think this gets called?
		this.hamburgerMenuBtn.removeEventListener('click', this.boundHamburgerMenuBtnClick)
		this.topBarLinks.forEach(link => {
			link.removeEventListener('click', this.boundTopBarLinkClick)
			link.removeEventListener('keydown', this.boundTopBarLinkKeydown)
		})
	}

	/**
	 * Modified clicks are left to the anchor's native behaviour so the parent page
	 * stays reachable in a new tab.
	 */
	private _handleTopBarLinkClick(e: MouseEvent): void {
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
			return
		}
		this._openMirroredPanel(e)
	}

	private _handleTopBarLinkKeydown(e: KeyboardEvent): void {
		// Enter fires a native click (handled above); intercept Space so it opens the
		// panel instead of scrolling the page, matching the menu item being mirrored.
		if (e.key === ' ' || e.key === 'Spacebar') {
			this._openMirroredPanel(e)
		}
	}

	/**
	 * A top-bar link is a mirror of a menu item, so it opens the menu and that item's
	 * flyout/panel in one action. An item with no panel is left to navigate to its href.
	 */
	private _openMirroredPanel(e: Event): void {
		const trigger = e.currentTarget as HTMLElement
		const navRef = trigger.dataset.navRef
		const panel = navRef
			? this.querySelector<NavPanel>(
					`gmx-nav-flyout[data-nav-ref="${navRef}"], gmx-nav-link-panel[data-nav-ref="${navRef}"]`
				)
			: null
		if (!panel) {
			return
		}
		e.preventDefault()
		// A newer request supersedes any still-pending one, so a slower earlier fetch
		// can't land afterwards and replace the panel actually asked for.
		this.querySelectorAll<NavPanel>('gmx-nav-flyout, gmx-nav-link-panel').forEach(
			other => other !== panel && other.cancelPendingOpen?.()
		)
		this.hamburgerMenuOpen(trigger)
		// Opens straight away if the panel has loaded, otherwise once its fetch lands.
		panel.open()
	}

	private _handleHamburgerMenuClick(): void {
		this.hamburgerMenuOpen()
	}

	public hamburgerMenuOpen(opener?: HTMLElement): void {
		// Focus goes back to whatever opened the menu, which is the hamburger button
		// unless a top-bar link opened it.
		this.menuOpener = opener ?? this.hamburgerMenuBtn
		if (window.browserDetectSingleton) {
			window.browserDetectSingleton.setScrollbarWidthOffset() /* Ensure layout doesn't shift */
		}

		/* Must add the class AND inline styles for apps that inject the nav and don't have the global CSS. */
		document.body.classList.add('scroll-disabled')
		document.body.style.height = '100%'
		document.body.style.overflow = 'hidden'

		this.hamburgerMenuContainer.classList.add('open')
		this.submenuSidebar.classList.add('open')
		this.hamburgerMenuBtn.setAttribute('aria-expanded', 'true')
		this.setBackgroundHandlerActive()

		// Focus the visible close control and trap Tab inside the open menu.
		this._menuFocusTarget()?.focus()
		this.addEventListener('keydown', this.boundTabTrap)

		this._topLevelNavBtns().forEach((button, index) =>
			setTimeout(() => {
				button.classList.add('animated')
			}, index * 100)
		)
	}

	/**
	 * The direct nav links of the sidebar menu, excluding links nested inside a
	 * flyout or link panel (those manage their own staggered reveal on open).
	 */
	private _topLevelNavBtns(): HTMLElement[] {
		return Array.from(this.submenuSidebar.querySelectorAll<HTMLElement>('.nav-btn')).filter(
			button => !button.closest('gmx-nav-flyout') && !button.closest('gmx-nav-link-panel')
		)
	}

	/**
	 * Single trap for the whole nav: wraps Tab/Shift+Tab across the sidebar and
	 * any open flyout/panel so focus can't escape to the logo or hamburger.
	 */
	private _handleTabTrap(e: KeyboardEvent): void {
		if (e.key !== 'Tab') {
			return
		}
		const focusables = this._menuFocusables()
		if (focusables.length === 0) {
			return
		}
		const first = focusables[0]
		const last = focusables[focusables.length - 1]
		// Use the event target (works inside a shadow root, unlike activeElement).
		const active = e.target as HTMLElement | null
		if (e.shiftKey && active === first) {
			e.preventDefault()
			last.focus()
		} else if (!e.shiftKey && active === last) {
			e.preventDefault()
			first.focus()
		}
	}

	/** Close button to focus on open: an open panel's, else the sidebar's. */
	private _menuFocusTarget(): HTMLElement | null {
		const openPanelClose = this.submenuSidebar.querySelector<HTMLElement>(
			'gmx-nav-flyout.open .flyout-close-btn, gmx-nav-link-panel.open .flyout-close-btn'
		)
		if (openPanelClose) {
			return openPanelClose
		}
		if (!this.hamburgerMenuCloseBtn.classList.contains('flyout-open')) {
			return this.hamburgerMenuCloseBtn
		}
		return this._menuFocusables()[0] ?? null
	}

	/** Focusable controls reachable in the open menu (sidebar + any open panel). */
	private _menuFocusables(): HTMLElement[] {
		return Array.from(this.submenuSidebar.querySelectorAll<HTMLElement>('button, a[href]')).filter(
			el =>
				!el.classList.contains('hidden') &&
				!el.classList.contains('flyout-open') &&
				!el.closest('gmx-nav-flyout:not(.open)') &&
				!el.closest('gmx-nav-link-panel:not(.open)')
		)
	}

	private _handleHamburgerMenuCloseBtnClick(): void {
		this.hamburgerMenuClose()
		this.setBackgroundHandlerInactive()
	}

	private _handleBackgroundClick(): void {
		this.hamburgerMenuClose()
		this.setBackgroundHandlerInactive()
	}

	public hamburgerMenuClose(): void {
		this.hamburgerMenuContainer.classList.remove('open')
		this.submenuSidebar.classList.remove('open')
		this.hamburgerMenuBtn.setAttribute('aria-expanded', 'false')

		document.body.classList.remove('scroll-disabled')
		document.body.style.height = ''
		document.body.style.overflow = ''

		this.removeEventListener('keydown', this.boundTabTrap)
		this._topLevelNavBtns().forEach(button => button.classList.remove('animated'))
		// A panel left open behind the closed menu keeps its triggers active, top-bar mirror included.
		this.querySelectorAll<NavPanel>('gmx-nav-flyout, gmx-nav-link-panel').forEach(panel => {
			panel.cancelPendingOpen?.()
			if (panel.classList.contains('open')) {
				panel.collapse?.()
			}
		})
		// collapse() above reactivates the handler for a menu that is still open; this one is not.
		this.setBackgroundHandlerInactive()
		// Return focus to the trigger on close.
		;(this.menuOpener ?? this.hamburgerMenuBtn).focus()
	}

	public setBackgroundHandlerActive(): void {
		this.backgroundHandler.classList.add('active')
	}

	public setBackgroundHandlerInactive(): void {
		this.backgroundHandler.classList.remove('active')
	}
}
if (!customElements.get('gmx-navigation')) {
	customElements.define('gmx-navigation', GMXNavigation)
}
